import React, { useEffect, useState, useMemo, useCallback } from "react";
import axiosInstance from "../../../redux/api";
import "./BulkEditStyle.css";
import {
  ACTIVE_FILTERS_KEY,
  DEFAULT_ACTIVE_FILTER_IDS,
  getAvailableFilterDefs,
  FilterPanel,
  FilterPickerBody,
} from "./BulkFilters";
import CellEditorModal from "./CellEditorModal";
import BulkUpdateFromFile from "./BulkUpdateFromFile";
import {
  PATCH_BATCH_SIZE,
  COST_FIELD_KEYS,
  REGULAR_PRICE_FIELD_KEYS,
  SALE_PRICE_FIELD_KEYS,
  buildPatchChange,
} from "./productBulkUpdateMapping";
import Select from "react-select";
import CommonPagination from "../../Common/Pagination/CommonPagination";
import swal from "sweetalert";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Spinner from "../../Layouts/Spinner";
import { uploadProductImage, pickPreviewUrl } from "../../Common/ImageUpload/handleProductImageUpload";
import { TaxonomyErrorReport } from "./TaxonomyErrors";
import ExportMenu from "../../Common/Export/ExportMenu";
import { usePermissions } from "../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../lib/permissions";

// Column-header sort: these columns compare numerically; every other column
// sorts alphabetically (A→Z asc / Z→A desc). Deciding by column — rather than
// parseFloat-ing each cell — keeps text columns that happen to start with a
// digit (e.g. "100ml Bottle") in proper alphabetical order.
const NUMERIC_SORT_KEYS = new Set([
  "cost", "regular_price", "sale_price", "quantity",
  "height", "width", "position", "menu_order", "order_in_view", "id",
]);

// Columns with no meaningful sort order (an image thumbnail / a URL). Clicking
// their header does nothing — sorting them would just reorder rows by raw URL
// string, which reads as random.
const NON_SORTABLE_KEYS = new Set(["image", "reference_link"]);

const INITIAL_FILTERS = {
  title_filter_type: "1",
  title_filter_value: "",
  category: "",
  sub_category: "",
  brand: "",
  type: "",
  regular_price_filter_type: ">",
  regular_price_value: "",
  // Cost operator filter — identical wire shape to regular/sale price
  // (cost_filter_type / cost_value). Gated behind cost permission server-side.
  cost_filter_type: ">",
  cost_value: "",
  sale_price_filter_type: ">",
  sale_price_value: "",
  stock_quantity_filter_type: ">",
  stock_quantity_value: "",
  status: "",
  b2b: "",
  position_filter_type: ">",
  position_value: "",
  barcode_filter_type: "1",
  barcode_filter_value: "",
};

const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const B2B_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const PRODUCT_OPTION_TYPE_OPTIONS = [
  { value: "Product", label: "Product" },
  { value: "Varient", label: "Varient" },
];

// Customizable filter registry + panel/picker components live in ./BulkFilters.

// Fields the Advanced Bulk Edit modal can edit. Keys must match a column key in
// `defaultColumns` so the modal can filter by the user's visible-columns set.
const BULK_EDITABLE_FIELDS = [
  { key: "name",                label: "Name",                type: "text" },
  { key: "ar_name",             label: "Arabic Name",         type: "text" },
  { key: "barcode",             label: "Barcode",             type: "text" },
  { key: "brand",               label: "Brand",               type: "text" },
  { key: "type",                label: "Type",                type: "select", optionsKey: "type" },
  { key: "ar_type",             label: "Arabic Type",         type: "text" },
  { key: "status",              label: "Status",              type: "select", options: STATUS_OPTIONS },
  { key: "description",         label: "Description",         type: "text" },
  { key: "ar_description",      label: "Arabic Description",  type: "text" },
  { key: "category",            label: "Category",            type: "text" },
  { key: "ar_category",         label: "Arabic Category",     type: "text" },
  { key: "sub_category",        label: "Sub Category",        type: "text" },
  { key: "ar_sub_category",     label: "Arabic Sub Category", type: "text" },
  { key: "cost",                label: "Cost Price",          type: "number" },
  { key: "regular_price",       label: "Regular Price",       type: "number" },
  { key: "sale_price",          label: "Sale Price",          type: "number" },
  { key: "is_b2b",              label: "B2B",                 type: "select", options: B2B_OPTIONS },
  { key: "quantity",            label: "Quantity",            type: "number" },
  { key: "color",               label: "Color",               type: "text" },
  { key: "size",                label: "Size",                type: "text" },
  { key: "product_option_type", label: "Product Option Type", type: "select", options: PRODUCT_OPTION_TYPE_OPTIONS },
  { key: "height",              label: "Height",              type: "number" },
  { key: "width",               label: "Width",               type: "number" },
  { key: "position",            label: "Position",            type: "number" },
  { key: "new_from",            label: "New From",            type: "date" },
  { key: "new_to",              label: "New To",              type: "date" },
];

// Strip HTML tags + decode common entities for in-cell display only.
// Edit mode keeps the raw HTML so saving doesn't lose markup.
const stripHtmlForDisplay = (input) => {
  if (input == null || input === "") return "";
  return String(input)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|&#160;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
};

const RICH_TEXT_COLUMNS = new Set(["description", "ar_description"]);
// Columns that open the roomy modal as PLAIN text (no HTML) — long product
// names are painful in a narrow cell. Rich vs plain decides the editor inside.
const PLAIN_MODAL_COLUMNS = new Set(["name", "ar_name"]);
const MODAL_EDIT_COLUMNS = new Set([...RICH_TEXT_COLUMNS, ...PLAIN_MODAL_COLUMNS]);

const BulkEdit = () => {
  const { hasMenuAccess, hasAction } = usePermissions();
  const canSeeCost = hasMenuAccess(MENU.PRODUCT_COSTS);
  const canEditCost = hasAction(MENU.PRODUCT_COSTS, PERMISSION.EDIT);
  const canSeeRegularPrice = hasMenuAccess(MENU.PRODUCT_REGULAR_PRICE);
  const canEditRegularPrice = hasAction(
    MENU.PRODUCT_REGULAR_PRICE,
    PERMISSION.EDIT,
  );
  const canSeeSalePrice = hasMenuAccess(MENU.PRODUCT_SALE_PRICE);
  const canEditSalePrice = hasAction(MENU.PRODUCT_SALE_PRICE, PERMISSION.EDIT);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [paginationMeta, setPaginationMeta] = useState({});
  // Rows per page. Backend caps `limit` at 200; default 100 balances fewer
  // page flips against the editable-grid render cost.
  const [pageSize, setPageSize] = useState(100);
  const [products, setProducts] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  // Tracks which columns the user has actually edited per row, so handleSubmit
  // only sends changed fields to bulk-update (sending unchanged fields exposes
  // the row to validation failures on data the user never touched).
  const [editedFields, setEditedFields] = useState({});
  const VISIBLE_COLUMNS_KEY = "bulkEditVisibleColumns";
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(VISIBLE_COLUMNS_KEY);
      if (saved) return new Set(JSON.parse(saved));
    } catch (e) { /* fall through */ }
    return new Set();
  });
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  // "Update from File" modal — bulk-update existing products from a spreadsheet
  // through the same PATCH admin/products/bulk-update endpoint as the grid.
  const [showFileUpdate, setShowFileUpdate] = useState(false);
  const [bulkEditValues, setBulkEditValues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [editingCell, setEditingCell] = useState({ id: null, key: null });
  // Long fields (description/ar_description as rich text; name/ar_name as plain
  // text) open a roomy modal editor instead of a cramped in-cell box.
  // Holds { id, key, label, rtl, value, mode } | null.
  const [cellEditor, setCellEditor] = useState(null);

  // Per-column sorting: { key: 'column_key', direction: 'asc' | 'desc' } or null
  const [sortConfig, setSortConfig] = useState(null);

  const [showRowCountPopup, setShowRowCountPopup] = useState(false);
  const [rowCount, setRowCount] = useState(1);

  const [showDuplicateCountPopup, setShowDuplicateCountPopup] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(1);
  const [showCatSubPopup, setShowCatSubPopup] = useState(false);
  // "en" | "ar" — which language the cat/sub modal opened from, so its option
  // labels render in that language. The selected values are language-agnostic
  // (we always key by the English name/title under the hood).
  const [catSubLang, setCatSubLang] = useState("en");
  const [tempCategory, setTempCategory] = useState([]);
  const [tempSubCategory, setTempSubCategory] = useState([]);
  const [currentEditingRow, setCurrentEditingRow] = useState(null);
  const [currentEditingKey, setCurrentEditingKey] = useState("");

  const [dropdownOptions, setDropdownOptions] = useState({
    type: [],
    product_option_type: [],
    color: [],
    size: [],
    occasion: [],
    category: [],
    brand: [],
  });

  // Default columns definition
  // Visible by default — mirrors PendingProducts DEFAULT_VISIBLE_COLUMN_KEYS
  const DEFAULT_VISIBLE_KEYS = new Set(
    [
      "barcode", "name", "ar_name", "description", "ar_description",
      "brand", "category", "ar_category", "sub_category", "ar_sub_category",
      "type", "ar_type", "is_b2b",
      ...(canSeeCost ? ["cost"] : []),
      ...(canSeeRegularPrice ? ["regular_price"] : []),
      ...(canSeeSalePrice ? ["sale_price"] : []),
      "quantity",
      "image", "reference_link",
    ]
  );

  const defaultColumns = [
    // --- Visible by default ---
    { key: "barcode",         label: "Barcode" },
    { key: "name",            label: "Name" },
    { key: "ar_name",         label: "Arabic Name" },
    { key: "description",     label: "Description" },
    { key: "ar_description",  label: "Arabic Description" },
    { key: "brand",           label: "Brand" },
    { key: "category",        label: "Category" },
    { key: "ar_category",     label: "Arabic Category" },
    { key: "sub_category",    label: "Sub Category" },
    { key: "ar_sub_category", label: "Arabic Sub Category" },
    { key: "type",            label: "Type" },
    { key: "ar_type",         label: "Arabic Type" },
    { key: "is_b2b",          label: "B2B" },
    ...(canSeeCost
      ? [{ key: "cost", label: "Cost Price" }]
      : []),
    ...(canSeeRegularPrice
      ? [{ key: "regular_price", label: "Regular Price" }]
      : []),
    ...(canSeeSalePrice
      ? [{ key: "sale_price", label: "Sale Price" }]
      : []),
    { key: "quantity",        label: "Quantity" },
    { key: "image",           label: "Image" },
    // NOTE: display-only rename to "Product Link". The underlying field is still
    // `reference_link` — same data, same wire contract; only the column name
    // changed. In the future the backend will expose a dedicated product link,
    // and this column should switch to read/write that field instead.
    { key: "reference_link",  label: "Product Link" },
    // --- Hidden by default; toggle via Columns ---
    { key: "product_option_type", label: "Product Option Type" },
    { key: "color",               label: "Color" },
    { key: "size",                label: "Size" },
    { key: "height",              label: "Height" },
    { key: "width",               label: "Width" },
    { key: "position",            label: "Position" },
    { key: "new_from",            label: "New From" },
    { key: "new_to",              label: "New To" },
    { key: "status",              label: "Status" },
  ];

  // Default column widths for compact e-commerce style table
  const getDefaultColumnWidth = (columnKey) => {
    const defaultWidths = {
      // Identification
      id: 80,
      // Names and titles
      name: 120,
      ar_name: 120,
      // Descriptions (compact by default, can be resized)
      description: 150,
      ar_description: 150,
      // Images
      image: 100,
      // Categories
      category: 120,
      sub_category: 120,
      // Brands and occasions
      brand: 100,
      occassion: 100,
      // Stock and inventory
      stock_quantity: 80,
      // Pricing
      cost: 100,
      regular_price: 100,
      sale_price: 100,
      // Product details
      type: 80,
      status: 90,
      product_option_type: 120,
      // Attributes
      color: 100,
      size: 100,
      height: 80,
      width: 80,
      quantity: 80,
      discount: 80,
      // Other
      weight: 80,
      rating: 80,
      menu_order: 90,
    };
    return defaultWidths[columnKey] || 100; // Default to 100px for unknown columns
  };

  const BULK_UPLOAD_COLUMNS_KEY = "bulkUploadColumnsOrderV3";

  const [columns, setColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(BULK_UPLOAD_COLUMNS_KEY);
      if (!saved) return defaultColumns;
      const parsed = JSON.parse(saved);
      const defaultByKey = new Map(defaultColumns.map((c) => [c.key, c]));
      const kept = parsed.filter((c) => defaultByKey.has(c.key)).map((c) => defaultByKey.get(c.key));
      const keptKeys = new Set(kept.map((c) => c.key));
      const fresh = defaultColumns.filter((c) => !keptKeys.has(c.key));
      return [...kept, ...fresh];
    } catch (e) {
      return defaultColumns;
    }
  });

  const [showFilters, setShowFilters] = useState(false);
  // Search box inside the Add-Filter modal.
  const [filterSearch, setFilterSearch] = useState("");
  // Which filters are currently shown on the panel. Persisted per-browser like
  // column visibility. The toolbar Search box is always available separately.
  const [activeFilterIds, setActiveFilterIds] = useState(() => {
    try {
      const saved = localStorage.getItem(ACTIVE_FILTERS_KEY);
      if (saved) {
        const arr = JSON.parse(saved);
        if (Array.isArray(arr)) return new Set(arr);
      }
    } catch (e) {
      /* fall through to defaults */
    }
    return new Set(DEFAULT_ACTIVE_FILTER_IDS);
  });

  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem("columnWidths");
    const currentColumns = (() => {
      const savedCols = localStorage.getItem(BULK_UPLOAD_COLUMNS_KEY);
      if (savedCols) {
        try {
          return JSON.parse(savedCols);
        } catch (e) {
          return defaultColumns;
        }
      }
      return defaultColumns;
    })();

    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Merge saved widths with defaults for any new columns
        const defaults = {};
        currentColumns.forEach((col) => {
          if (!(col.key in parsed)) {
            defaults[col.key] = getDefaultColumnWidth(col.key);
          }
        });
        return { ...defaults, ...parsed };
      } catch (e) {
        // If parsing fails, use defaults
        const defaults = {};
        currentColumns.forEach((col) => {
          defaults[col.key] = getDefaultColumnWidth(col.key);
        });
        return defaults;
      }
    }
    // Initialize with defaults
    const defaults = {};
    currentColumns.forEach((col) => {
      defaults[col.key] = getDefaultColumnWidth(col.key);
    });
    return defaults;
  });

  const [resizingColumn, setResizingColumn] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);

  const [subCategoryOptions, setSubCategoryOptions] = useState([]);

  // Holds per-row failures returned by the bulk-update PATCH (taxonomy
  // mismatches, margin violations, etc.). Rendered in a dedicated modal so the
  // user can read the full message and jump to the relevant admin page.
  const [bulkUpdateErrors, setBulkUpdateErrors] = useState([]);
  // Ids that just persisted — drives the brief green flash on the row.
  const [savedRowIds, setSavedRowIds] = useState(new Set());
  // {[id]: errorMessage} — drives the red border + inline error tooltip.
  const [rowErrors, setRowErrors] = useState({});
  // {current, total} when a save spans multiple 500-row batches; null otherwise.
  const [batchProgress, setBatchProgress] = useState(null);
  // Drives the "Updated 2m ago" indicator in the page header.
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  // Re-rendered every 30s so the relative time label stays fresh while idle.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    // Only set defaults if no saved visibility exists
    if (visibleColumns.size === 0) setVisibleColumns(DEFAULT_VISIBLE_KEYS);
    fetchDropdowns();
    fetchSubCategories();
    // Initial product load (page 1) so the grid + pagination populate on mount.
    fetchProducts(INITIAL_FILTERS);
  }, []);

  // Warn before leaving with unsaved edits or rows marked for deletion
  useEffect(() => {
    const hasEdits =
      Object.keys(editedFields).length > 0 ||
      products.some((p) => p.status === "Delete");
    const handler = (e) => {
      if (hasEdits) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [editedFields, products]);

  const fetchSubCategories = async (categoryId = "") => {
    try {
      const res = await axiosInstance.get("admin/sub-categories", {
        params: { page: 1, search_key: "", status: "", category_ids: categoryId },
      });
      const list = res.data.response || [];
      setSubCategoryOptions(
        list.map((item) => ({ id: item.id, value: item.title, label: item.title }))
      );
    } catch (err) {
      console.error("Error fetching sub categories:", err);
      setSubCategoryOptions([]);
    }
  };

  useEffect(() => {
    if (
      filters.category &&
      dropdownOptions.category &&
      dropdownOptions.category.length > 0
    ) {
      const selectedCategory = dropdownOptions.category.find(
        (cat) => cat.name === filters.category
      );
      if (selectedCategory) {
        fetchSubCategories(selectedCategory.id.toString());
      }
    } else if (!filters.category) {
      // Category cleared — reset sub-category dropdown and filter value
      setSubCategoryOptions([]);
      setFilters((prev) => prev.sub_category ? { ...prev, sub_category: "" } : prev);
    }
  }, [filters.category, dropdownOptions.category]);

  const fetchDropdowns = async () => {
    try {
      const [colorRes, sizeRes, categoryRes, brandRes] =
        await Promise.all([
          axiosInstance.get("admin/colors", { params: { pages: "" } }),
          axiosInstance.get("admin/sizes", { params: { pages: "" } }),
          axiosInstance.get("admin/categories", { params: { pages: "", type: "1 2 3" } }),
          axiosInstance.get("admin/brands", { params: { pages: "" } }),
        ]);

      const mapOptions = (arr) =>
        Array.isArray(arr)
          ? arr.map((item) => ({
              id: item.id,
              name: item.name || item.title || "",
              ar_name: item.ar_name || item.ar_title || "",
            }))
          : [];

      // Fetch type list from API instead of hardcoding
      let typeList = [];
      try {
        const typeRes = await axiosInstance.get("admin/types");
        typeList = mapOptions(typeRes?.data?.response);
      } catch (err) {
        console.error("Error fetching types:", err);
      }

      setDropdownOptions({
        product_option_type: [
          { id: 1, name: "Product" },
          { id: 2, name: "Variant" },
        ],
        type: typeList,
        color: mapOptions(colorRes?.data?.response),
        size: mapOptions(sizeRes?.data?.response),
        category: mapOptions(categoryRes?.data?.response),
        brand: mapOptions(brandRes?.data?.response),
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchProducts = async (passedFilters = filters, limit = pageSize) => {
    setIsLoadingProducts(true);
    try {
      // Only send non-empty filter params.
      // Also strip _filter_type keys whose paired value is empty — sending
      // e.g. regular_price_filter_type="> " without regular_price_value can
      // cause the API to interpret it as "> 0" and silently exclude products.
      const typePairs = {
        title_filter_type: "title_filter_value",
        barcode_filter_type: "barcode_filter_value",
        regular_price_filter_type: "regular_price_value",
        sale_price_filter_type: "sale_price_value",
        cost_filter_type: "cost_value",
        stock_quantity_filter_type: "stock_quantity_value",
        position_filter_type: "position_value",
      };
      const cleanParams = Object.fromEntries(
        Object.entries(passedFilters).filter(([k, v]) => {
          if (v === "" || v == null) return false;
          // Drop the type selector if its paired value field is empty
          if (k in typePairs) {
            const pairedValue = passedFilters[typePairs[k]];
            return pairedValue !== "" && pairedValue != null;
          }
          return true;
        })
      );
      // The numeric operator filters (price/stock) use "==" for equality on
      // the backend, but the UI selects send "=". Normalize before the request
      // so the "equals" option actually filters instead of being a no-op.
      for (const typeKey of Object.keys(typePairs)) {
        if (cleanParams[typeKey] === "=") cleanParams[typeKey] = "==";
      }
      // `type` filters by comma-separated Type names — same convention as
      // category/brand, so it passes straight through cleanParams as-is.
      // Page size + opt into the COUNT(*) so the response carries `total` /
      // `total_pages`, which drives numbered pagination and the "X of N" label.
      cleanParams.limit = limit;
      cleanParams.with_total = true;
      const res = await axiosInstance.get(
        "admin/temp-products/bulk-updates",
        { params: cleanParams }
      );
      const rawProducts = res.data.response.products || [];
      const normalized = rawProducts.map((p) => {
        // This list endpoint (admin/temp-products/bulk-updates) returns a
        // flattened image representation — `p.image` / `p.main_image` (scalar
        // 800w URL) plus `p.image_urls` (dict, often null). No nested
        // `images[]` array. Reconstruct the array shape the rest of the page
        // expects so the render path and the subsequent PUT payload stay
        // uniform with the product detail endpoint (which does return
        // images[]).
        const arrayImages = Array.isArray(p.images) ? p.images : [];
        // Main image is the first entry in images[] by convention (see
        // MediaSection); fall back to the flattened scalar fields.
        const scalarUrl =
          arrayImages[0]?.image_url ||
          arrayImages[0]?.image_urls?.["800w"] ||
          p.main_image ||
          p.image ||
          null;
        const images = arrayImages.length > 0
          ? arrayImages.map((img, idx) => ({
              ...img,
              image_urls:
                img.image_urls ||
                (img.image_url ? { "800w": img.image_url } : null),
              sort_order: typeof img.sort_order === "number" ? img.sort_order : idx,
            }))
          : scalarUrl
            ? [{
                image_url: scalarUrl,
                image_urls: p.image_urls || { "800w": scalarUrl },
                sort_order: 0,
              }]
            : [];
        const primary = images[0] || null;
        return {
          ...p,
          is_b2b: p.is_b2b === 1 || p.is_b2b === "1" || p.is_b2b === true ? "yes" : "no",
          images,
          // Lifted from images[0] for render convenience; images[] stays the source of truth.
          image_urls: primary?.image_urls || null,
          // Raw URL used as a render fallback if image_urls lookup fails.
          main_image: scalarUrl,
        };
      });
      setProducts(normalized);
      setPaginationMeta(res.data.response.pagination_meta || {});
      setLastFetchedAt(Date.now());
    } catch (error) {
      console.error("Error fetching products:", error);

      if (error.response) {
        console.log("Response status:", error.response.status);
        console.log("Response data:", error.response.data);
      } else {
        console.log("No response received:", error.message);
      }

      setProducts([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // useCallback so these stay referentially stable and the memoized
  // <FilterPanel> isn't re-rendered by unrelated parent state (product edits,
  // the relative-time tick). handleFilterChange uses a functional update, so it
  // needs no deps; the submit/keydown pair depends only on `filters`.
  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleFilterSubmit = useCallback(() => {
    const resetPage = { ...filters, page: 1 };
    setFilters(resetPage);
    fetchProducts(resetPage);
  }, [filters]);

  // Submit filters on Enter from any filter field
  const handleFilterKeyDown = useCallback((e) => {
    if (e.key === "Enter") handleFilterSubmit();
  }, [handleFilterSubmit]);

  // Clears all filter *values* but keeps the user's chosen active-filter layout
  // intact (so they can re-enter values without re-adding the fields).
  const handleClearAllFilters = () => {
    setFilters(INITIAL_FILTERS);
    fetchProducts(INITIAL_FILTERS);
  };

  // Clear a single filter from the header chip strip. `keys` is the list of
  // filter keys that should be reset together (e.g. paired _filter_type + _value
  // fields). Re-fetches with the cleared payload so the grid stays in sync.
  const clearFilterByKeys = (keys) => {
    const next = { ...filters };
    for (const k of keys) {
      if (k in INITIAL_FILTERS) next[k] = INITIAL_FILTERS[k];
      else delete next[k];
    }
    setFilters(next);
    fetchProducts(next);
  };

  // Count of active user-facing filter values (ignores _filter_type keys and empty strings)
  const activeFilterCount = Object.entries(filters).filter(
    ([k, v]) => v !== "" && v != null && !k.endsWith("_filter_type") && k !== "page" && k !== "search_key"
  ).length;

  // Compact filter chip descriptors for the page-header context strip. Each
  // chip clears just its own key(s) when dismissed. Order matches the filter
  // panel so users can match chips to their inputs visually.
  const headerFilterChips = useMemo(() => {
    const chips = [];
    const op = (t) => ({ ">": ">", "<": "<", "=": "=", "1": "contains", "2": "is", "3": "not", "4": "starts", "5": "ends" }[t] || t);
    if (filters.title_filter_value) {
      chips.push({ id: "title", label: "Title", value: `${op(filters.title_filter_type)} ${filters.title_filter_value}`, keys: ["title_filter_value"] });
    }
    if (filters.barcode_filter_value) {
      chips.push({ id: "barcode", label: "Barcode", value: `${op(filters.barcode_filter_type)} ${filters.barcode_filter_value}`, keys: ["barcode_filter_value"] });
    }
    if (filters.category) chips.push({ id: "category", label: "Category", value: filters.category, keys: ["category", "sub_category"] });
    if (filters.sub_category) {
      const sub = (subCategoryOptions || []).find((s) => s.value === filters.sub_category);
      chips.push({ id: "sub_category", label: "Sub", value: sub?.label || filters.sub_category, keys: ["sub_category"] });
    }
    if (filters.brand) chips.push({ id: "brand", label: "Brand", value: filters.brand, keys: ["brand"] });
    if (filters.type) chips.push({ id: "type", label: "Type", value: filters.type, keys: ["type"] });
    if (filters.status) chips.push({ id: "status", label: "Status", value: ({ "1": "Active", "2": "Inactive" }[filters.status] || filters.status), keys: ["status"] });
    if (filters.b2b) chips.push({ id: "b2b", label: "B2B", value: filters.b2b === "yes" ? "Yes" : "No", keys: ["b2b"] });
    if (filters.regular_price_value) chips.push({ id: "rp", label: "Regular", value: `${op(filters.regular_price_filter_type)} ${filters.regular_price_value}`, keys: ["regular_price_value"] });
    if (filters.cost_value) chips.push({ id: "cost", label: "Cost", value: `${op(filters.cost_filter_type)} ${filters.cost_value}`, keys: ["cost_value"] });
    if (filters.sale_price_value) chips.push({ id: "sp", label: "Sale", value: `${op(filters.sale_price_filter_type)} ${filters.sale_price_value}`, keys: ["sale_price_value"] });
    if (filters.stock_quantity_value) chips.push({ id: "stock", label: "Stock", value: `${op(filters.stock_quantity_filter_type)} ${filters.stock_quantity_value}`, keys: ["stock_quantity_value"] });
    if (filters.position_value) chips.push({ id: "position", label: "Position", value: `${op(filters.position_filter_type)} ${filters.position_value}`, keys: ["position_value"] });
    return chips;
  }, [filters, subCategoryOptions]);

  const formatTimeAgo = (ts) => {
    if (!ts) return "";
    const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (s < 10) return "just now";
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return new Date(ts).toLocaleDateString();
  };

  // Discard all unsaved cell edits + pending deletions by re-fetching server
  // state. We confirm because the action is destructive (in-memory) and
  // irreversible.
  const handleDiscardChanges = async () => {
    const editedIds = new Set(Object.keys(editedFields));
    const deletePending = products.filter(
      (p) => p.status === "Delete" && !editedIds.has(String(p.id))
    ).length;
    const dirty = editedIds.size + deletePending;
    if (dirty === 0) return;
    const confirmed = await swal({
      title: "Discard unsaved changes?",
      text: `${dirty} row${dirty === 1 ? "" : "s"} will revert to the last saved state.`,
      icon: "warning",
      buttons: ["Cancel", "Discard"],
      dangerMode: true,
    });
    if (!confirmed) return;
    setEditedFields({});
    setRowErrors({});
    setBulkUpdateErrors([]);
    fetchProducts(filters);
  };

  // ---- Export ----
  // The /admin/exports/products endpoint accepts only:
  //   search_key, status, from_date, to_date, in_stock,
  //   category_id, sub_category_id, occasion_id, brand_id, type_id
  // Any bulk-edit filter outside that set (price/menu_order/stock-quantity
  // operators, title/barcode operators, status, extra filters) cannot be sent
  // — the export will be a superset of the on-screen rows when those are active.
  const buildExportFilters = () => {
    const out = {};
    // search_key — toolbar search wins; otherwise fall back to "contains"
    // operators on title / barcode (other operators would be lossy).
    const search =
      (filters.search_key || "").trim() ||
      (filters.title_filter_type === "1" && filters.title_filter_value) ||
      (filters.barcode_filter_type === "1" && filters.barcode_filter_value) ||
      "";
    if (search) out.search_key = search;

    if (filters.category) {
      const cat = (dropdownOptions.category || []).find((c) => c.name === filters.category);
      if (cat?.id) out.category_id = cat.id;
    }
    if (filters.sub_category) {
      const sub = (subCategoryOptions || []).find((s) => s.value === filters.sub_category);
      if (sub?.id) out.sub_category_id = sub.id;
    }
    if (filters.brand) {
      const b = (dropdownOptions.brand || []).find((c) => c.name === filters.brand);
      if (b?.id) out.brand_id = b.id;
    }
    if (filters.type) {
      const t = (dropdownOptions.type || []).find((c) => c.name === filters.type);
      if (t?.id) out.type_id = t.id;
    }

    // Mirror the on-screen column picker into the export. Unknown slugs (image,
    // reference_link, color, size, …) are silently dropped server-side.
    const visibleKeys = columns
      .filter((c) => visibleColumns.has(c.key))
      .map((c) => c.key);
    if (visibleKeys.length > 0) out.columns = visibleKeys.join(",");

    return out;
  };

  // Filters present on the bulk-edit screen that the export endpoint can't
  // honor. Used to show a warning chip on the Export button.
  const exportUnsupportedActive = (() => {
    if (filters.regular_price_value || filters.sale_price_value) return true;
    if (filters.cost_value) return true;
    if (filters.stock_quantity_value || filters.position_value) return true;
    if (filters.status) return true;
    if (filters.b2b) return true;
    if (filters.title_filter_value && filters.title_filter_type !== "1") return true;
    if (filters.barcode_filter_value && filters.barcode_filter_type !== "1") return true;
    return false;
  })();

  const handleSelectRow = (id) => {
    setSelectedRows((prev) => {
      const updated = new Set(prev);
      updated.has(id) ? updated.delete(id) : updated.add(id);
      return new Set(updated);
    });
  };

  // Open the roomy modal for a long cell (rich for descriptions, plain for
  // names). Reads the row's current value as the editor's starting point.
  const openCellEditor = (id, key) => {
    const row = products.find((p) => p.id === id);
    const col = columns.find((c) => c.key === key);
    setCellEditor({
      id,
      key,
      label: col?.label || key,
      rtl: key.startsWith("ar_"),
      value: row?.[key] ?? "",
      mode: RICH_TEXT_COLUMNS.has(key) ? "rich" : "plain",
    });
  };

  const closeCellEditor = useCallback(() => setCellEditor(null), []);
  // Apply writes the edited value back through the normal cell-change path
  // (dirty tracking + row selection), then closes.
  const applyCellEditor = (value) => {
    if (cellEditor) handleCellChange(cellEditor.id, cellEditor.key, value);
    setCellEditor(null);
  };

  const startEditing = (id, key) => {
    // VIEW-only price scopes show a value but the cell isn't editable. The
    // backend would 422 on a write either way; short-circuit at click time so
    // the cell never enters edit mode.
    if (key === "cost" && !canEditCost) return;
    if (key === "regular_price" && !canEditRegularPrice) return;
    if (key === "sale_price" && !canEditSalePrice) return;
    // Long fields open the modal editor (click or keyboard), never an in-cell box.
    if (MODAL_EDIT_COLUMNS.has(key)) {
      openCellEditor(id, key);
      return;
    }
    setEditingCell({ id, key });
  };

  const stopEditing = () => {
    setEditingCell({ id: null, key: null });
  };

  // Keyboard navigation: Tab → next visible cell, Enter → cell below, Escape → cancel
  const handleCellKeyDown = (e, productId, colKey) => {
    if (e.key === "Escape") {
      stopEditing();
      return;
    }
    if (e.key === "Tab" || e.key === "Enter") {
      e.preventDefault();
      const visibleCols = columns.filter((c) => visibleColumns.has(c.key));
      const colIdx = visibleCols.findIndex((c) => c.key === colKey);
      const rowIdx = sortedProducts.findIndex((p) => p.id === productId);

      if (e.key === "Tab") {
        // Move to next/prev visible column; wrap to next/prev row
        const step = e.shiftKey ? -1 : 1;
        let nextCol = colIdx + step;
        let nextRow = rowIdx;
        if (nextCol >= visibleCols.length) { nextCol = 0; nextRow++; }
        if (nextCol < 0) { nextCol = visibleCols.length - 1; nextRow--; }
        if (nextRow >= 0 && nextRow < sortedProducts.length) {
          startEditing(sortedProducts[nextRow].id, visibleCols[nextCol].key);
        } else {
          stopEditing();
        }
      } else {
        // Enter: move to same column, next row
        const nextRow = rowIdx + (e.shiftKey ? -1 : 1);
        if (nextRow >= 0 && nextRow < sortedProducts.length) {
          startEditing(sortedProducts[nextRow].id, colKey);
        } else {
          stopEditing();
        }
      }
    }
  };

  const handleCellChange = (id, key, value) => {
    // Type is relational — editing either the English or the Arabic cell sets
    // the *same* Type, so keep both columns in lock-step. The English `type`
    // (name) is the one that persists as type_id on save; ar_type rides along
    // for display.
    const extraFields = {};
    const extraKeys = [];
    if (key === "type") {
      const matchedType = dropdownOptions.type?.find((t) => t.name === value);
      extraFields.ar_type = matchedType?.ar_name || "";
      extraKeys.push("ar_type");
    } else if (key === "ar_type") {
      // Reverse direction: user picked from the Arabic Type dropdown (options
      // labeled by ar_name). Resolve back to the canonical Type so `type` /
      // type_id stay correct.
      const matchedType = dropdownOptions.type?.find(
        (t) => (t.ar_name || t.name) === value
      );
      if (matchedType) {
        extraFields.type = matchedType.name;
        extraKeys.push("type");
      }
    }

    setProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [key]: value, ...extraFields } : p))
    );
    // Record the edit so handleSubmit knows which fields to send
    setEditedFields((prev) => {
      const rowEdits = new Set(prev[id] || []);
      rowEdits.add(key);
      extraKeys.forEach((k) => rowEdits.add(k));
      return { ...prev, [id]: rowEdits };
    });
    // Auto-select the row when a cell is edited
    setSelectedRows((prev) => {
      if (prev.has(id)) return prev;
      const updated = new Set(prev);
      updated.add(id);
      return updated;
    });
  };

  // Per-column sort handler. A→Z is the default, so the first click on a column
  // flips to Z→A; further clicks toggle Z→A ⇄ A→Z. There is no unsorted/off
  // state in the cycle.
  const handleColumnSort = (columnKey) => {
    setSortConfig((prev) => {
      if (!prev || prev.key !== columnKey) return { key: columnKey, direction: "desc" };
      return { key: columnKey, direction: prev.direction === "desc" ? "asc" : "desc" };
    });
  };

  const sortedProducts = useMemo(() => {
    if (!sortConfig) return products;

    const { key, direction } = sortConfig;
    return [...products].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];

      const aEmpty = aVal === null || aVal === undefined || aVal === "";
      const bEmpty = bVal === null || bVal === undefined || bVal === "";
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;

      let cmp;
      if (NUMERIC_SORT_KEYS.has(key)) {
        cmp = (Number(aVal) || 0) - (Number(bVal) || 0);
      } else {
        // Alphabetical A→Z. `numeric: true` gives natural ordering ("item2"
        // before "item10"); `sensitivity: "base"` makes it case-insensitive.
        cmp = String(aVal).localeCompare(String(bVal), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }
      return direction === "asc" ? cmp : -cmp;
    });
  }, [products, sortConfig]);

  const getSortIcon = (columnKey) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return <i className="bi bi-arrow-down-up ms-2 text-muted" style={{ fontSize: "12px" }}></i>;
    }
    if (sortConfig.direction === "asc") {
      return <i className="bi bi-sort-down ms-2 text-primary" style={{ fontSize: "12px" }}></i>;
    }
    return <i className="bi bi-sort-down-alt ms-2 text-primary" style={{ fontSize: "12px" }}></i>;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    // Reset transient per-row signals from the previous save attempt.
    setSavedRowIds(new Set());
    setRowErrors({});
    try {
      const selectedProducts = products.filter((p) => selectedRows.has(p.id));

      if (selectedProducts.length === 0) {
        swal({
          title: "No Rows Selected",
          text: "Please select at least one row to submit.",
          icon: "warning",
        });
        return;
      }

      // Rows marked for deletion still go to admin/products/bulk-delete;
      // status=Delete is intentionally not exposed to the bulk-update PATCH.
      const rowsToDelete = selectedProducts.filter(
        (p) => p.status === "Delete"
      );
      const rowsToUpdate = selectedProducts.filter(
        (p) => p.status !== "Delete"
      );

      let deletedNames = [];
      let deleteFailures = [];

      if (rowsToDelete.length > 0) {
        try {
          const res = await axiosInstance.post("admin/products/bulk-delete", {
            ids: rowsToDelete.map((r) => r.id),
          });
          const deletedCount = res?.data?.deleted ?? rowsToDelete.length;
          if (deletedCount >= rowsToDelete.length) {
            deletedNames = rowsToDelete.map((r) => r.name || `#${r.id}`);
          } else {
            deletedNames = rowsToDelete
              .slice(0, deletedCount)
              .map((r) => r.name || `#${r.id}`);
            deleteFailures = rowsToDelete
              .slice(deletedCount)
              .map((r) => r.name || `#${r.id}`);
          }
        } catch (delErr) {
          console.error("Bulk delete failed", delErr);
          deleteFailures = rowsToDelete.map((r) => r.name || `#${r.id}`);
        }
      }

      let updateMessage = "";
      let updateFailed = []; // [{id, error}] — straight from the endpoint
      let updateSuccessIds = []; // int[] of rows that persisted
      let skippedNoEdits = 0;
      let skippedNewRowNames = []; // new/duplicated rows can't be inserted via bulk-update
      let imageMessage = "";
      let imageFailedNames = [];

      if (rowsToUpdate.length > 0) {
        const rowsWithEdits = rowsToUpdate.filter((p) => {
          const edits = editedFields[p.id];
          return edits && edits.size > 0;
        });
        skippedNoEdits = rowsToUpdate.length - rowsWithEdits.length;

        // Images use the nested images[] schema and ride a separate per-row PUT.
        const hasScalarEdits = (p) =>
          Array.from(editedFields[p.id] || new Set()).some((k) => k !== "image");
        const rowsWithScalarEdits = rowsWithEdits.filter(hasScalarEdits);
        const rowsWithImageEdits = rowsWithEdits.filter((p) =>
          (editedFields[p.id] || new Set()).has("image")
        );

        if (rowsWithScalarEdits.length > 0) {
          const persistedRows = rowsWithScalarEdits.filter((p) =>
            Number.isInteger(p.id)
          );
          skippedNewRowNames = rowsWithScalarEdits
            .filter((p) => !Number.isInteger(p.id))
            .map((p) => p.name || "(unsaved row)");

          if (persistedRows.length > 0) {
            const changes = persistedRows
              .map((p) => {
                const editedKeys = editedFields[p.id] || new Set();
                // Strip any price keys the user lacks EDIT for — the backend
                // 422s the whole bulk-update otherwise.
                const sanitizedKeys = new Set(
                  Array.from(editedKeys).filter((k) => {
                    if (!canEditCost && COST_FIELD_KEYS.has(k)) return false;
                    if (!canEditRegularPrice && REGULAR_PRICE_FIELD_KEYS.has(k))
                      return false;
                    if (!canEditSalePrice && SALE_PRICE_FIELD_KEYS.has(k))
                      return false;
                    return true;
                  }),
                );
                return buildPatchChange(p, sanitizedKeys, dropdownOptions);
              })
              .filter(Boolean);

            const batches = [];
            for (let i = 0; i < changes.length; i += PATCH_BATCH_SIZE) {
              batches.push(changes.slice(i, i + PATCH_BATCH_SIZE));
            }

            try {
              for (let i = 0; i < batches.length; i++) {
                if (batches.length > 1) {
                  setBatchProgress({ current: i + 1, total: batches.length });
                }
                const res = await axiosInstance.patch(
                  "admin/products/bulk-update",
                  { changes: batches[i] }
                );
                const data = res?.data?.response || {};
                updateSuccessIds.push(...(data.successful_ids || []));
                updateFailed.push(...(data.failed || []));
              }
            } catch (err) {
              if (err?.response?.status === 403) {
                swal({
                  title: "No Permission",
                  text: "You don't have permission to bulk-edit products.",
                  icon: "error",
                });
                return;
              }
              throw err;
            } finally {
              setBatchProgress(null);
            }

            const total = persistedRows.length;
            const ok = updateSuccessIds.length;
            const failed = updateFailed.length;
            updateMessage = failed > 0
              ? `${ok} of ${total} saved. ${failed} failed — see highlighted rows.`
              : `${ok} product(s) updated.`;
          }
        }

        if (rowsWithImageEdits.length > 0) {
          const results = await Promise.allSettled(
            rowsWithImageEdits.map((p) => {
              const imagesPayload = (p.images || []).map((img, idx) => ({
                image_url: img.image_url || img.image_urls?.["800w"] || null,
                image_urls: img.image_urls,
                sort_order: idx,
              }));
              return axiosInstance.put("admin/products/", {
                id: p.id,
                images: imagesPayload,
              });
            })
          );
          const succeeded = results.filter((r) => r.status === "fulfilled").length;
          imageFailedNames = results
            .map((r, i) =>
              r.status === "rejected"
                ? rowsWithImageEdits[i].name || `#${rowsWithImageEdits[i].id}`
                : null
            )
            .filter(Boolean);
          if (succeeded > 0) {
            imageMessage = `Images saved for ${succeeded} product(s).`;
          }
          if (imageFailedNames.length > 0) {
            const firstReject = results.find((r) => r.status === "rejected");
            console.error(
              "Image PUT failed:",
              firstReject?.reason?.response?.data || firstReject?.reason
            );
          }
        }
      }

      const summaryLines = [];
      if (deletedNames.length > 0) {
        summaryLines.push(`${deletedNames.length} product(s) deleted.`);
      }
      if (deleteFailures.length > 0) {
        summaryLines.push(`Delete failed: ${deleteFailures.join(", ")}`);
      }
      if (updateMessage) {
        summaryLines.push(updateMessage);
      }
      if (skippedNewRowNames.length > 0) {
        summaryLines.push(
          `${skippedNewRowNames.length} new row(s) skipped — bulk edit only updates existing products.`
        );
      }
      if (imageMessage) {
        summaryLines.push(imageMessage);
      }
      if (imageFailedNames.length > 0) {
        summaryLines.push(`Image save failed: ${imageFailedNames.join(", ")}`);
      }
      if (skippedNoEdits > 0) {
        summaryLines.push(
          `${skippedNoEdits} selected row(s) had no edits and were skipped.`
        );
      }

      const hasFailures =
        deleteFailures.length > 0 ||
        updateFailed.length > 0 ||
        imageFailedNames.length > 0;

      // Enrich failed entries with the row name so the dedicated error modal
      // can show "Chanel Bleu — margin too low" instead of just "#23491".
      if (updateFailed.length > 0) {
        const enriched = updateFailed.map((f) => {
          const row = rowsToUpdate.find((p) => p.id === f.id);
          return {
            id: f.id,
            identifier: row?.name || (f.id != null ? `#${f.id}` : ""),
            error: f.error || "Save failed.",
          };
        });
        setBulkUpdateErrors(enriched);
      }

      swal({
        title: hasFailures ? "Completed with Errors" : "Update Complete",
        text: summaryLines.join("\n") || "No changes submitted.",
        icon: hasFailures ? "warning" : "success",
      });

      // --- Per-row visual state + dirty-bit reconciliation ---
      const successSet = new Set(updateSuccessIds);
      const errorMap = {};
      updateFailed.forEach((f) => {
        if (f.id != null) errorMap[f.id] = f.error || "Save failed.";
      });
      setSavedRowIds(successSet);
      setRowErrors(errorMap);

      // Drop dirty/selection state for rows that persisted; keep failed rows
      // dirty + selected so the user can fix-and-resave without re-checking.
      setEditedFields((prev) => {
        const next = { ...prev };
        for (const id of successSet) delete next[id];
        return next;
      });
      setSelectedRows((prev) => {
        const next = new Set(prev);
        for (const id of successSet) next.delete(id);
        return next;
      });

      // Brief green flash, then clear so the row returns to its normal look.
      if (successSet.size > 0) {
        setTimeout(() => {
          setSavedRowIds((prev) => {
            const next = new Set(prev);
            successSet.forEach((id) => next.delete(id));
            return next;
          });
        }, 3000);
      }

      // No refetch — successful rows already hold the new values in state, and
      // refetching would scroll-jump and re-query 500+ products for nothing.
      // Deletes are the one thing not in memory anymore, so prune those.
      if (deletedNames.length > 0) {
        const deletedIds = new Set(
          rowsToDelete.slice(0, deletedNames.length).map((r) => r.id)
        );
        setProducts((prev) => prev.filter((p) => !deletedIds.has(p.id)));
        // Drop the now-gone rows from selection + dirty state so the header
        // pills don't keep counting products that no longer exist.
        setSelectedRows((prev) => {
          const next = new Set(prev);
          for (const id of deletedIds) next.delete(id);
          return next;
        });
        setEditedFields((prev) => {
          const next = { ...prev };
          for (const id of deletedIds) delete next[id];
          return next;
        });
      }
    } catch (err) {
      console.error(err);
      swal({ title: "Error", text: "Failed to submit changes.", icon: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddRows = () => {
    const newRows = Array.from({ length: rowCount }, () => ({
      id: `new_${crypto.randomUUID()}`,
    }));
    setProducts((prev) => [...prev, ...newRows]);
    setShowRowCountPopup(false);
  };

  const handleDuplicateRows = () => {
    const rowsToDuplicate = products.filter((p) => selectedRows.has(p.id));
    const newDuplicates = rowsToDuplicate.flatMap((row) =>
      Array.from({ length: duplicateCount }, () => ({
        ...row,
        id: `dup_${crypto.randomUUID()}`,
      }))
    );
    setProducts((prev) => [...prev, ...newDuplicates]);
    setShowDuplicateCountPopup(false);
  };

  const handleColumnToggle = (columnKey) => {
    const newVisible = new Set(visibleColumns);
    if (newVisible.has(columnKey)) {
      newVisible.delete(columnKey);
    } else {
      newVisible.add(columnKey);
    }
    setVisibleColumns(newVisible);
    localStorage.setItem(VISIBLE_COLUMNS_KEY, JSON.stringify([...newVisible]));
  };

  const handleApplyBulkEdit = () => {
    const changedEntries = Object.entries(bulkEditValues).filter(
      ([_, v]) => v !== ""
    );
    if (changedEntries.length === 0) {
      setShowBulkEditModal(false);
      return;
    }
    const changed = Object.fromEntries(changedEntries);
    const changedKeys = Object.keys(changed);

    // Auto-sync ar_type when type is bulk-edited
    if ("type" in changed) {
      const matchedType = dropdownOptions.type?.find((t) => t.name === changed.type);
      changed.ar_type = matchedType?.ar_name || "";
      if (!changedKeys.includes("ar_type")) changedKeys.push("ar_type");
    }

    const updated = products.map((product) =>
      selectedRows.has(product.id) ? { ...product, ...changed } : product
    );
    setProducts(updated);

    // Record bulk edits so handleSubmit knows which fields changed per row
    setEditedFields((prev) => {
      const next = { ...prev };
      selectedRows.forEach((id) => {
        const rowEdits = new Set(next[id] || []);
        changedKeys.forEach((k) => rowEdits.add(k));
        next[id] = rowEdits;
      });
      return next;
    });

    setShowBulkEditModal(false);
    // Keep rows selected so the user can immediately Save Changes
  };

  const handleSelectAll = () => {
    if (selectedRows.size === products.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(products.map((p) => p.id)));
    }
  };

  // New unified image upload: POST /uploads/images returns multi-size variants,
  // stored on the row as both `images[]` (authoritative for writes) and a
  // lifted `image_urls` (for render convenience).
  const handleProductImageSelect = async (file, id) => {
    if (!file) return;
    try {
      const { urls } = await uploadProductImage(file);
      const imagesPayload = [{ image_urls: urls, sort_order: 0 }];
      setProducts((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, image_urls: urls, images: imagesPayload } : p
        )
      );
      setEditedFields((prev) => {
        const rowEdits = new Set(prev[id] || []);
        rowEdits.add("image");
        return { ...prev, [id]: rowEdits };
      });
      setSelectedRows((prev) => {
        if (prev.has(id)) return prev;
        const updated = new Set(prev);
        updated.add(id);
        return updated;
      });
    } catch (err) {
      console.error("Product image upload failed", err);
    }
  };

  const openProductImagePicker = (id) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => handleProductImageSelect(e.target.files[0], id);
    input.click();
  };

  const handleResizeStart = (e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    const currentWidth =
      columnWidths[columnKey] || getDefaultColumnWidth(columnKey);
    setResizingColumn(columnKey);
    setResizeStartX(e.clientX);
    setResizeStartWidth(currentWidth);
  };

  useEffect(() => {
    const handleResizeMove = (e) => {
      if (!resizingColumn) return;

      const diff = e.clientX - resizeStartX;
      const newWidth = Math.max(80, resizeStartWidth + diff);

      setColumnWidths((prev) => ({ ...prev, [resizingColumn]: newWidth }));
    };

    const handleResizeEnd = () => {
      // Persist only once on mouseup instead of every pixel
      setColumnWidths((prev) => {
        localStorage.setItem("columnWidths", JSON.stringify(prev));
        return prev;
      });
      setResizingColumn(null);
      document.body.classList.remove("resizing-column");
    };

    if (resizingColumn) {
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);
      document.body.classList.add("resizing-column");
    }

    return () => {
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeEnd);
      document.body.classList.remove("resizing-column");
    };
  }, [resizingColumn, resizeStartX, resizeStartWidth]);

  const handleDeleteSelected = () => {
    if (selectedRows.size === 0) {
      swal({
        title: "No Selection",
        text: "Please select at least one product to delete.",
        icon: "warning",
      });
      return;
    }

    swal({
      title: "Mark for Deletion?",
      text: `${selectedRows.size} product(s) will be marked for deletion. Click "Save Changes" to confirm.`,
      icon: "warning",
      buttons: ["Cancel", "Yes, Mark for Delete"],
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) {
        setProducts((prev) =>
          prev.map((product) =>
            selectedRows.has(product.id)
              ? { ...product, status: "Delete" }
              : product
          )
        );
        // Keep rows selected so they get submitted
      }
    });
  };

  const handlePageChange = (newPage) => {
    const newFilters = { ...filters, page: newPage };
    setFilters(newFilters);
    fetchProducts(newFilters);
  };

  // Changing page size resets to page 1 and refetches. The new size is passed
  // explicitly because the `pageSize` state update isn't visible to the
  // fetchProducts closure synchronously.
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    const newFilters = { ...filters, page: 1 };
    setFilters(newFilters);
    fetchProducts(newFilters, newSize);
  };

  // Combined unsaved-change count driving the Save/Discard affordances. Rows
  // marked for deletion are unsaved work too — they only persist on Save (via
  // admin/products/bulk-delete) and never enter `editedFields`. A row can be
  // both edited and marked for deletion; it counts once.
  const pendingChangeCount = useMemo(() => {
    const editedIds = new Set(Object.keys(editedFields));
    let count = editedIds.size;
    for (const p of products) {
      if (p.status === "Delete" && !editedIds.has(String(p.id))) count += 1;
    }
    return count;
  }, [editedFields, products]);
  const totalCount =
    paginationMeta.total ?? paginationMeta.total_count ?? products.length;

  // --- Customizable filters ---
  // Only filters the user is permitted to see are offered in the picker/panel.
  const availableFilterDefs = useMemo(
    () => getAvailableFilterDefs({ canSeeCost, canSeeRegularPrice, canSeeSalePrice }),
    [canSeeCost, canSeeRegularPrice, canSeeSalePrice],
  );

  // The active subset, in registry order. Stable across product-grid re-renders
  // (only changes when the permitted set or the active ids change), which lets
  // the memoized <FilterPanel> skip re-rendering while the user edits cells.
  const activeDefs = useMemo(
    () => availableFilterDefs.filter((d) => activeFilterIds.has(d.id)),
    [availableFilterDefs, activeFilterIds],
  );

  // Option sources for the relational dropdowns, keyed to match def.optionsKey.
  // Computed here (not as render-time consts) so the object identity is stable
  // unless its inputs change.
  const filterOptionSources = useMemo(() => {
    const toOpts = (arr) => (arr || []).map((i) => ({ value: i.name, label: i.name }));
    return {
      category: toOpts(dropdownOptions.category),
      brand: toOpts(dropdownOptions.brand),
      type: toOpts(dropdownOptions.type),
      sub_category: subCategoryOptions,
    };
  }, [dropdownOptions.category, dropdownOptions.brand, dropdownOptions.type, subCategoryOptions]);

  const persistActiveFilters = (set) => {
    try {
      localStorage.setItem(ACTIVE_FILTERS_KEY, JSON.stringify([...set]));
    } catch (e) {
      /* localStorage unavailable — keep in-memory only */
    }
  };

  const addFilter = useCallback((id) => {
    setActiveFilterIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      persistActiveFilters(next);
      return next;
    });
  }, []);

  // Removing a filter also clears its value(s) and refetches if it was actually
  // constraining results, so a hidden filter can never keep silently filtering.
  const removeFilter = useCallback((def) => {
    setActiveFilterIds((prev) => {
      const next = new Set(prev);
      next.delete(def.id);
      persistActiveFilters(next);
      return next;
    });
    const hadValue = filters[def.valueKey] !== "" && filters[def.valueKey] != null;
    const nextFilters = { ...filters };
    for (const k of def.resetKeys) {
      nextFilters[k] = k in INITIAL_FILTERS ? INITIAL_FILTERS[k] : "";
    }
    setFilters(nextFilters);
    if (hadValue) fetchProducts(nextFilters);
  }, [filters]);

  const toggleFilter = useCallback((def) => {
    if (activeFilterIds.has(def.id)) removeFilter(def);
    else addFilter(def.id);
  }, [activeFilterIds, removeFilter, addFilter]);

  return (
    <div className="bulk-edit-page">
      {/* SaaS-style sticky page header */}
      <header className="bulk-page-header">
        <div className="bulk-page-header__row bulk-page-header__row--main">
          <div className="bulk-page-header__identity">
            <nav className="bulk-page-header__crumbs" aria-label="Breadcrumb">
              <span>Catalog</span>
              <i className="bi bi-chevron-right" aria-hidden="true" />
              <span className="bulk-page-header__crumbs-current">Bulk Edit</span>
            </nav>
            <div className="bulk-page-header__title-row">
              <h1 className="bulk-page-header__title">Bulk Edit Products</h1>
              {pendingChangeCount > 0 && (
                <span className="bulk-page-header__dirty-dot" aria-label="Unsaved changes">
                  <span className="bulk-page-header__dirty-dot-pulse" />
                </span>
              )}
            </div>
          </div>

          <div className="bulk-page-header__stats" role="group" aria-label="Workspace stats">
            <span className="bph-pill bph-pill--total" title="Products matching the current filters">
              <i className="bi bi-box-seam" aria-hidden="true" />
              <strong>{totalCount.toLocaleString()}</strong>
              <em>total</em>
            </span>
            <span className="bph-pill bph-pill--visible" title="Rows currently on screen">
              <i className="bi bi-eye" aria-hidden="true" />
              <strong>{products.length.toLocaleString()}</strong>
              <em>visible</em>
            </span>
            {paginationMeta.total_pages > 1 && (
              <span className="bph-pill bph-pill--page" title="Current page of total pages">
                <i className="bi bi-file-earmark-text" aria-hidden="true" />
                <strong>{paginationMeta.current_page || 1}/{paginationMeta.total_pages}</strong>
                <em>page</em>
              </span>
            )}
            {selectedRows.size > 0 && (
              <span className="bph-pill bph-pill--selected" title="Rows selected for bulk action">
                <i className="bi bi-check2-square" aria-hidden="true" />
                <strong>{selectedRows.size.toLocaleString()}</strong>
                <em>selected</em>
              </span>
            )}
            {pendingChangeCount > 0 && (
              <span className="bph-pill bph-pill--unsaved" title="Rows with unsaved edits or pending deletion">
                <i className="bi bi-pencil-square" aria-hidden="true" />
                <strong>{pendingChangeCount.toLocaleString()}</strong>
                <em>unsaved</em>
              </span>
            )}
          </div>

          <div className="bulk-page-header__actions">
            <button
              type="button"
              className="bulk-btn-icon"
              onClick={() => fetchProducts(filters)}
              disabled={isLoadingProducts}
              title="Refresh"
              aria-label="Refresh"
            >
              {isLoadingProducts ? (
                <span style={{ transform: "scale(0.55)", display: "inline-flex" }}>
                  <Spinner />
                </span>
              ) : (
                <i className="bi bi-arrow-clockwise" />
              )}
            </button>
            <button
              type="button"
              className="bulk-btn-ghost"
              onClick={handleDiscardChanges}
              disabled={pendingChangeCount === 0 || isSubmitting}
              title={pendingChangeCount === 0 ? "Nothing to discard" : `Discard ${pendingChangeCount} unsaved change${pendingChangeCount === 1 ? "" : "s"}`}
            >
              Discard
            </button>
            <button
              type="button"
              className="bulk-btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting || pendingChangeCount === 0}
              title={pendingChangeCount === 0 ? "No unsaved changes" : `Save ${pendingChangeCount} pending change${pendingChangeCount === 1 ? "" : "s"}`}
            >
              {isSubmitting ? (
                batchProgress ? `Saving ${batchProgress.current}/${batchProgress.total}…` : "Saving…"
              ) : pendingChangeCount > 0 ? (
                <>
                  <i className="bi bi-check2" />
                  {`Save ${pendingChangeCount} ${pendingChangeCount === 1 ? "change" : "changes"}`}
                </>
              ) : (
                <>
                  <i className="bi bi-check2" />
                  Saved
                </>
              )}
            </button>
          </div>
        </div>

        <div className="bulk-page-header__row bulk-page-header__row--context">
          <div className="bulk-page-header__chips">
            {headerFilterChips.length > 0 ? (
              <>
                <span className="bulk-page-header__chips-label">
                  <i className="bi bi-funnel-fill" aria-hidden="true" />
                  {activeFilterCount} {activeFilterCount === 1 ? "filter" : "filters"}
                </span>
                {headerFilterChips.map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    className="bulk-page-header__chip"
                    onClick={() => clearFilterByKeys(chip.keys)}
                    title={`Remove ${chip.label} filter`}
                  >
                    <span className="bulk-page-header__chip-key">{chip.label}</span>
                    <span className="bulk-page-header__chip-value">{chip.value}</span>
                    <i className="bi bi-x" aria-hidden="true" />
                  </button>
                ))}
                <button
                  type="button"
                  className="bulk-page-header__chip-clear"
                  onClick={handleClearAllFilters}
                >
                  Clear all
                </button>
              </>
            ) : (
              <span className="bulk-page-header__chips-empty">
                <i className="bi bi-funnel" aria-hidden="true" />
                No filters applied
              </span>
            )}
          </div>
          <div className="bulk-page-header__sync">
            {isLoadingProducts ? (
              <span className="bulk-page-header__sync-status bulk-page-header__sync-status--loading">
                <i className="bi bi-arrow-clockwise" /> Refreshing…
              </span>
            ) : lastFetchedAt ? (
              <span className="bulk-page-header__sync-status">
                <i className="bi bi-clock-history" /> Updated {formatTimeAgo(lastFetchedAt)}
              </span>
            ) : null}
          </div>
        </div>
      </header>

      {/* Filter Panel */}
        <div className="">
          <div className="woo-filters-panel">
            <div className="woo-filters-header">
              <h3 onClick={() => setShowFilters(!showFilters)}>
                Product Filters
                {activeFilterCount > 0 && (
                  <span className="filters-count-pill">
                    {activeFilterCount} active
                  </span>
                )}
              </h3>
              <div className="woo-filters-header-right">
                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    className="filters-clear-link"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearAllFilters();
                    }}
                  >
                    Clear all
                  </button>
                )}
                <i
                  onClick={() => setShowFilters(!showFilters)}
                  className={`bi ${
                    showFilters ? "bi-chevron-up" : "bi-chevron-down"
                  } toggle-icon`}
                ></i>
              </div>
            </div>

            {showFilters && (
              <>
                <div className="woo-filters-body">
                  <FilterPanel
                    activeDefs={activeDefs}
                    filters={filters}
                    optionSources={filterOptionSources}
                    onChange={handleFilterChange}
                    onRemove={removeFilter}
                    onKeyDown={handleFilterKeyDown}
                  />
                </div>

                <div className="woo-filters-footer d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-outline-secondary me-2"
                    data-bs-toggle="modal"
                    data-bs-target="#addFilterModal"
                  >
                    <i className="bi bi-plus-circle me-1"></i>
                    Add Filter
                  </button>
                  <div className="d-flex gap-2">
                    {activeFilterCount > 0 && (
                      <button
                        className="btn btn-outline-danger"
                        onClick={handleClearAllFilters}
                      >
                        Clear All
                      </button>
                    )}
                    <button
                      className="btn woo-btn-primary"
                      onClick={handleFilterSubmit}
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Extra Filters Modal */}
          <div
            className="modal fade"
            id="addFilterModal"
            tabIndex="-1"
            aria-labelledby="addFilterModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered modal-lg filter-picker-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="addFilterModalLabel">
                    Add Filter
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  ></button>
                </div>
                <FilterPickerBody
                  availableDefs={availableFilterDefs}
                  activeFilterIds={activeFilterIds}
                  search={filterSearch}
                  onSearchChange={setFilterSearch}
                  onToggle={toggleFilter}
                />
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    data-bs-dismiss="modal"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      {(() => {
        return (
      <div className="card bulk-card">
        <div className="bulk-toolbar">
          <div className="bulk-toolbar-search">
            <i className="bi bi-search bulk-toolbar-search-icon"></i>
            <input
              type="text"
              placeholder="Search products..."
              value={filters.search_key || ""}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, search_key: e.target.value }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") handleFilterSubmit();
              }}
            />
            {filters.search_key && (
              <button
                type="button"
                className="bulk-toolbar-search-clear"
                title="Clear search"
                onClick={() => {
                  const newFilters = { ...filters, search_key: "" };
                  setFilters(newFilters);
                  fetchProducts(newFilters);
                }}
              >
                ×
              </button>
            )}
          </div>

          <div className="bulk-toolbar-spacer" />

          <div className="bulk-toolbar-actions">
            <ExportMenu
              resource="products"
              params={buildExportFilters()}
              className="bulk-btn-ghost"
              title={
                exportUnsupportedActive
                  ? "Some active filters (price, operator-based title/barcode, status, stock quantity, etc.) aren't supported by the export — the file may include more rows than what's on screen."
                  : "Export filtered products"
              }
              leftIcon={<i className="bi bi-download"></i>}
              rightAdornment={
                exportUnsupportedActive ? (
                  <i
                    className="bi bi-exclamation-triangle-fill"
                    aria-label="Some filters won't apply to the export"
                    style={{ marginLeft: 6, color: "#d97706" }}
                  />
                ) : null
              }
            />

            <button
              type="button"
              className="bulk-btn-ghost"
              onClick={() => setShowFileUpdate(true)}
              title="Bulk-update existing products by uploading an Excel/CSV file"
            >
              <i className="bi bi-file-earmark-arrow-up"></i>
              Update from File
            </button>

            <button
              type="button"
              className="bulk-btn-ghost"
              onClick={() => setShowColumnModal(true)}
            >
              <i className="bi bi-layout-three-columns"></i>
              Columns
            </button>

            <button
              type="button"
              className="bulk-btn-ghost"
              disabled={selectedRows.size === 0}
              onClick={() => {
                setBulkEditValues((prev) =>
                  Object.fromEntries(
                    Object.entries(prev).filter(([k]) => visibleColumns.has(k))
                  )
                );
                setShowBulkEditModal(true);
              }}
              title={
                selectedRows.size === 0
                  ? "Select rows first"
                  : `Bulk-edit ${selectedRows.size} selected row${
                      selectedRows.size === 1 ? "" : "s"
                    }`
              }
            >
              <i className="bi bi-pencil-square"></i>
              Advanced Bulk Edit
            </button>

            <button
              type="button"
              className="bulk-btn-ghost bulk-btn-danger"
              disabled={selectedRows.size === 0}
              onClick={handleDeleteSelected}
              title={
                selectedRows.size === 0
                  ? "Select rows first"
                  : `Delete ${selectedRows.size} selected row${
                      selectedRows.size === 1 ? "" : "s"
                    }`
              }
            >
              <i className="bi bi-trash"></i>
              Delete Selected
            </button>
          </div>
        </div>

        <div className="card-body">

          {showRowCountPopup && (
            <div className="mb-3">
              <label>How many rows?</label>
              <input
                type="number"
                value={rowCount}
                onChange={(e) => setRowCount(Number(e.target.value))}
                className="form-control"
                min={1}
              />
              <button className="btn btn-primary mt-2" onClick={handleAddRows}>
                Add Rows
              </button>
            </div>
          )}

          {showDuplicateCountPopup && (
            <div className="mb-3">
              <label>How many copies per row?</label>
              <input
                type="number"
                value={duplicateCount}
                onChange={(e) => setDuplicateCount(Number(e.target.value))}
                className="form-control"
                min={1}
              />
              <button
                className="btn btn-warning mt-2"
                onClick={handleDuplicateRows}
              >
                Duplicate
              </button>
            </div>
          )}

          <div className="table-responsive sticky-table-wrapper">
            <table className="table table-bordered woo-products-table">
              <DragDropContext
                onDragEnd={(result) => {
                  if (!result.destination) return;

                  // Get visible columns to map indices correctly
                  const visibleCols = columns.filter((col) =>
                    visibleColumns.has(col.key)
                  );

                  // Get the dragged column key
                  const draggedColumnKey = visibleCols[result.source.index].key;

                  // Find the actual indices in the full columns array
                  const sourceIndex = columns.findIndex(
                    (col) => col.key === draggedColumnKey
                  );
                  const destColumnKey =
                    visibleCols[result.destination.index].key;
                  const destIndex = columns.findIndex(
                    (col) => col.key === destColumnKey
                  );

                  // Reorder the full columns array
                  const newColumns = Array.from(columns);
                  const [moved] = newColumns.splice(sourceIndex, 1);
                  newColumns.splice(destIndex, 0, moved);

                  localStorage.setItem(
                    BULK_UPLOAD_COLUMNS_KEY,
                    JSON.stringify(newColumns)
                  );
                  setColumns(newColumns);
                }}
              >
                <Droppable droppableId="columns" direction="horizontal">
                  {(provided) => (
                    <thead
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="table-light"
                    >
                      <tr>
                        <th style={{ width: "50px" }}>
                          <input
                            type="checkbox"
                            checked={
                              selectedRows.size === products.length &&
                              products.length > 0
                            }
                            onChange={handleSelectAll}
                          />
                        </th>

                        {columns
                          .filter((column) => visibleColumns.has(column.key))
                          .map((column, index) => (
                            <Draggable
                              key={column.key}
                              draggableId={column.key}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <th
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`sortable-header ${
                                    snapshot.isDragging ? "dragging" : ""
                                  } ${
                                    resizingColumn === column.key
                                      ? "resizing"
                                      : ""
                                  } ${sortConfig?.key === column.key ? "sorted" : ""}`}
                                  style={{
                                    ...provided.draggableProps.style,
                                    backgroundColor: snapshot.isDragging
                                      ? "#f8f9fa"
                                      : "white",
                                    boxShadow: snapshot.isDragging
                                      ? "0 2px 8px rgba(0,0,0,0.2)"
                                      : "none",
                                    transition:
                                      resizingColumn === column.key
                                        ? "none"
                                        : "all 0.2s ease",
                                    width: `${
                                      columnWidths[column.key] ||
                                      getDefaultColumnWidth(column.key)
                                    }px`,
                                    minWidth: `${
                                      columnWidths[column.key] ||
                                      getDefaultColumnWidth(column.key)
                                    }px`,
                                    maxWidth: `${
                                      columnWidths[column.key] ||
                                      getDefaultColumnWidth(column.key)
                                    }px`,
                                    position: "relative",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      paddingRight: "10px",
                                    }}
                                  >
                                    {/* Column label - click to sort (A→Z / Z→A).
                                        Non-sortable columns (image/link) are inert. */}
                                    <div
                                      onClick={
                                        NON_SORTABLE_KEYS.has(column.key)
                                          ? undefined
                                          : () => handleColumnSort(column.key)
                                      }
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        flex: 1,
                                        cursor: NON_SORTABLE_KEYS.has(column.key)
                                          ? "default"
                                          : "pointer",
                                        userSelect: "none",
                                      }}
                                    >
                                      {column.label}
                                      {!NON_SORTABLE_KEYS.has(column.key) &&
                                        getSortIcon(column.key)}
                                    </div>

                                    {/* Drag handle - reorder columns (kept separate
                                        from the sort click so a click never triggers
                                        a drag-reorder) */}
                                    <div
                                      {...provided.dragHandleProps}
                                      title="Drag to reorder column"
                                      style={{
                                        marginLeft: "8px",
                                        cursor: snapshot.isDragging
                                          ? "grabbing"
                                          : "grab",
                                      }}
                                    >
                                      <i
                                        className="bi bi-arrows-move text-secondary"
                                        style={{ fontSize: "12px" }}
                                      ></i>
                                    </div>
                                  </div>

                                  {/* Resize handle */}
                                  <div
                                    className="column-resize-handle"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleResizeStart(e, column.key);
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    title="Drag to resize column"
                                  />
                                </th>
                              )}
                            </Draggable>
                          ))}
                        {provided.placeholder}
                      </tr>
                    </thead>
                  )}
                </Droppable>
              </DragDropContext>
              <tbody>
                {/* ✅ Use sorted products instead of filtered products */}
                {sortedProducts.map((product) => {
                  const rowError = rowErrors[product.id];
                  const rowSaved = savedRowIds.has(product.id);
                  const rowClasses = [
                    "editable-row",
                    product.status === "Delete" ? "deleted-row" : "",
                    rowSaved ? "bulk-row-saved" : "",
                    rowError ? "bulk-row-error" : "",
                  ]
                    .filter(Boolean)
                    .join(" ");
                  return (
                  <tr key={product.id} className={rowClasses}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedRows.has(product.id)}
                        onChange={() => handleSelectRow(product.id)}
                      />
                      {rowError && (
                        <i
                          className="bi bi-exclamation-triangle-fill text-danger ms-2"
                          title={rowError}
                          style={{ cursor: "help" }}
                        />
                      )}
                    </td>
                    {columns
                      .filter((col) => visibleColumns.has(col.key))
                      .map((col) => {
                        const editingSelectCell =
                          editingCell.id === product.id &&
                          editingCell.key === col.key &&
                          ["occasion"].includes(col.key);
                        const isCellEdited = editedFields[product.id]?.has(col.key);
                        return (
                          <td
                            key={`${product.id}-${col.key}`}
                            className={`${editingSelectCell ? "td-select-edit" : ""} ${isCellEdited ? "cell-edited" : ""}`}
                            style={{
                              width: `${
                                columnWidths[col.key] ||
                                getDefaultColumnWidth(col.key)
                              }px`,
                              minWidth: `${
                                columnWidths[col.key] ||
                                getDefaultColumnWidth(col.key)
                              }px`,
                              maxWidth: `${
                                columnWidths[col.key] ||
                                getDefaultColumnWidth(col.key)
                              }px`,
                              overflow: editingSelectCell
                                ? "visible"
                                : "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              wordBreak: "normal",
                              verticalAlign: "middle",
                            }}
                            onClick={() => {
                              if (
                                col.key === "category" ||
                                col.key === "sub_category" ||
                                col.key === "ar_category" ||
                                col.key === "ar_sub_category"
                              ) {
                                // Arabic cells open the same modal as their
                                // English twins — they edit the same relation.
                                // The flag just controls which language the
                                // option labels render in.
                                setCatSubLang(
                                  col.key.startsWith("ar_") ? "ar" : "en"
                                );
                                const currentCategories = (
                                  product.category || ""
                                )
                                  .split(",")
                                  .map((v) => v.trim())
                                  .filter((v) => v);

                                setTempCategory(currentCategories);

                                const currentSubCategories = (
                                  product.sub_category || ""
                                )
                                  .split(",")
                                  .map((v) => v.trim())
                                  .filter((v) => v);

                                setTempSubCategory(currentSubCategories);

                                setCurrentEditingRow(product.id);

                                const selectedIDs = dropdownOptions.category
                                  .filter((opt) =>
                                    currentCategories.includes(opt.name)
                                  )
                                  .map((opt) => opt.id);

                                const payload = {
                                  category_ids: selectedIDs.join(","),
                                  page: 1,
                                  search_key: "",
                                  status: "",
                                };

                                axiosInstance
                                  .get("admin/sub-categories", { params: payload })
                                  .then((res) => {
                                    setDropdownOptions((prev) => ({
                                      ...prev,
                                      sub_category: res.data.response || [],
                                    }));
                                  });

                                setShowCatSubPopup(true);
                              } else if (
                                ["occasion"].includes(col.key)
                              ) {
                                startEditing(product.id, col.key);
                              } else if (col.key !== "image") {
                                startEditing(product.id, col.key);
                              }
                            }}
                          >
                            {editingCell.id === product.id &&
                            editingCell.key === col.key ? (
                              ["occasion", "brand"].includes(col.key) ? (
                                  <Select
                                    isMulti
                                    className="bulk-table-react-select"
                                    menuPortalTarget={
                                      typeof document !== "undefined"
                                        ? document.body
                                        : null
                                    }
                                    styles={{
                                      menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 10000,
                                      }),
                                    }}
                                    value={dropdownOptions[col.key]
                                      ?.filter((opt) =>
                                        (product[col.key] || "")
                                          .split(",")
                                          .map((v) => v.trim())
                                          .includes(opt.name)
                                      )
                                      .map((opt) => ({
                                        value: opt.name,
                                        label: opt.name,
                                      }))}
                                    options={dropdownOptions[col.key]?.map(
                                      (opt) => ({
                                        value: opt.name,
                                        label: opt.name,
                                      })
                                    )}
                                    onChange={(selected) => {
                                      const values = selected
                                        .map((s) => s.value)
                                        .join(", ");
                                      handleCellChange(
                                        product.id,
                                        col.key,
                                        values
                                      );
                                    }}
                                    onBlur={stopEditing}
                                    autoFocus
                                  />
                              ) : [
                                  "is_b2b",
                                  "is_cake",
                                  "status",
                                  "type",
                                  "ar_type",
                                  "product_option_type",
                                  "color",
                                  "size",
                                ].includes(col.key) ? (
                                <div className="cell-relative">
                                  <select
                                    className="popup-input"
                                    value={product[col.key] || ""}
                                    onChange={(e) =>
                                      handleCellChange(
                                        product.id,
                                        col.key,
                                        e.target.value
                                      )
                                    }
                                    onBlur={stopEditing}
                                    onKeyDown={(e) => handleCellKeyDown(e, product.id, col.key)}
                                    autoFocus
                                  >
                                    <option value="">Select</option>
                                    {col.key === "status" && (
                                      <>
                                        <option value="Active">Active</option>
                                        <option value="Inactive">
                                          Inactive
                                        </option>
                                      </>
                                    )}
                                    {col.key === "is_b2b" && (
                                      <>
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                      </>
                                    )}
                                    {col.key === "is_cake" && (
                                      <>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                      </>
                                    )}
                                    {col.key === "type" &&
                                      dropdownOptions.type?.map((opt) => (
                                        <option key={opt.id} value={opt.name}>
                                          {opt.name}
                                        </option>
                                      ))}
                                    {col.key === "ar_type" &&
                                      dropdownOptions.type?.map((opt) => (
                                        <option
                                          key={opt.id}
                                          value={opt.ar_name || opt.name}
                                        >
                                          {opt.ar_name || opt.name}
                                        </option>
                                      ))}
                                    {col.key === "product_option_type" &&
                                      dropdownOptions.product_option_type?.map(
                                        (opt) => (
                                          <option key={opt.id} value={opt.name}>
                                            {opt.name}
                                          </option>
                                        )
                                      )}
                                    {["color", "size"].includes(col.key) &&
                                      dropdownOptions[col.key]?.map((opt) => (
                                        <option key={opt.id} value={opt.name}>
                                          {opt.name}
                                        </option>
                                      ))}
                                  </select>
                                </div>
                              ) : ["description", "ar_description"].includes(
                                  col.key
                                ) ? (
                                <div className="cell-relative">
                                  <textarea
                                    className="popup-input"
                                    value={product[col.key] || ""}
                                    onChange={(e) =>
                                      handleCellChange(
                                        product.id,
                                        col.key,
                                        e.target.value
                                      )
                                    }
                                    onBlur={stopEditing}
                                    onKeyDown={(e) => {
                                      if (e.key === "Escape") stopEditing();
                                      // Tab navigates; Enter inserts newline (default)
                                      if (e.key === "Tab") handleCellKeyDown(e, product.id, col.key);
                                    }}
                                    autoFocus
                                    rows={3}
                                  ></textarea>
                                </div>
                              ) : (
                                <div className="cell-relative">
                                  <input
                                    className="popup-input"
                                    type="text"
                                    value={product[col.key] ?? ""}
                                    onChange={(e) =>
                                      handleCellChange(
                                        product.id,
                                        col.key,
                                        e.target.value
                                      )
                                    }
                                    onBlur={stopEditing}
                                    onKeyDown={(e) => handleCellKeyDown(e, product.id, col.key)}
                                    autoFocus
                                  />
                                </div>
                              )
                            ) : col.key === "image" ? (
                              <div
                                onClick={() => openProductImagePicker(product.id)}
                                style={{ cursor: "pointer" }}
                              >
                                {(() => {
                                  const primary = product.images?.[0];
                                  const src =
                                    pickPreviewUrl(primary, "200w") ||
                                    product.image_urls?.["200w"] ||
                                    product.image_urls?.["400w"] ||
                                    product.image_urls?.["800w"] ||
                                    product.main_image ||
                                    product.image ||
                                    null;
                                  return src ? (
                                    <img
                                      src={src}
                                      alt="product"
                                      style={{ width: "50px", height: "50px", objectFit: "cover" }}
                                      onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                        e.currentTarget.nextSibling.style.display = "inline";
                                      }}
                                    />
                                  ) : null;
                                })()}
                                <span
                                  style={{
                                    color: "var(--nysmah-accent)",
                                    textDecoration: "underline",
                                    fontSize: "11px",
                                    display:
                                      product.images?.[0] ||
                                      product.image_urls ||
                                      product.main_image ||
                                      product.image
                                        ? "none"
                                        : "inline",
                                  }}
                                >
                                  Click to Upload
                                </span>
                              </div>
                            ) : col.key === "reference_link" ? (
                              product[col.key] ? (
                                <a
                                  href={product[col.key]}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ fontSize: "11px", color: "#0d6efd" }}
                                >
                                  View Link
                                </a>
                              ) : (
                                <span
                                  style={{ cursor: "pointer", color: "#aaa", fontSize: "11px" }}
                                  onClick={() => startEditing(product.id, col.key)}
                                >
                                  Add Link
                                </span>
                              )
                            ) : (
                              (() => {
                                let raw = product[col.key];
                                // Arabic Category has no auto-sync (unlike
                                // ar_type): the upload can leave the English
                                // name (or nothing) in ar_category. When the
                                // stored value is empty or is just the English
                                // category name, derive Arabic from the
                                // category's own Arabic name (categories store
                                // it as ar_title; the dropdown loader exposes it
                                // as ar_name). A genuine Arabic value from the
                                // Excel is left untouched. Match is trimmed and
                                // case-insensitive so minor sheet/DB casing
                                // differences still resolve.
                                if (col.key === "ar_category" && product.category) {
                                  const enCat = String(product.category).trim();
                                  const stored = String(raw ?? "").trim();
                                  if (!stored || stored === enCat) {
                                    const derived = enCat
                                      .split(",")
                                      .map((n) => n.trim())
                                      .filter(Boolean)
                                      .map((n) => {
                                        const lc = n.toLowerCase();
                                        return (
                                          dropdownOptions.category?.find(
                                            (o) =>
                                              String(o.name).trim().toLowerCase() ===
                                              lc
                                          )?.ar_name || ""
                                        );
                                      })
                                      .filter(Boolean)
                                      .join(", ");
                                    if (derived) raw = derived;
                                  }
                                }
                                const isRich = RICH_TEXT_COLUMNS.has(col.key);
                                const usesModal = MODAL_EDIT_COLUMNS.has(col.key);
                                const display = isRich
                                  ? stripHtmlForDisplay(raw)
                                  : raw ?? "";
                                // `0` (e.g. a quantity/stock/price of zero) is a
                                // real value and must render as "0", not blank —
                                // so don't lean on `display ||` truthiness below.
                                const isEmpty =
                                  display === "" ||
                                  display === null ||
                                  display === undefined;
                                return (
                                  <span
                                    style={{
                                      display: "inline-block",
                                      maxWidth: "100%",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      verticalAlign: "middle",
                                    }}
                                    title={display}
                                  >
                                    {usesModal && (
                                      <i
                                        className="bi bi-arrows-angle-expand bulk-desc-cell-icon"
                                        aria-hidden="true"
                                      />
                                    )}
                                    {isEmpty
                                      ? usesModal && (
                                          <em className="bulk-desc-cell-empty">
                                            Click to edit
                                          </em>
                                        )
                                      : display}
                                  </span>
                                );
                              })()
                            )}
                          </td>
                        );
                      })}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {products.length > 0 && (
            <div className="bulk-pagination-footer d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="bulk-pagination-status text-muted">
                {(() => {
                  const cur = paginationMeta.current_page || filters.page || 1;
                  const lim = paginationMeta.per_page || pageSize;
                  const start = (cur - 1) * lim + 1;
                  const end = start + products.length - 1;
                  return `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${totalCount.toLocaleString()}`;
                })()}
              </div>

              {paginationMeta.total_pages > 1 && (
                <CommonPagination
                  currentPage={paginationMeta.current_page || 1}
                  totalPages={paginationMeta.total_pages}
                  onPageChange={handlePageChange}
                />
              )}

              <div className="bulk-pagination-size d-flex align-items-center gap-2">
                <span className="text-muted">Rows:</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "auto" }}
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  aria-label="Rows per page"
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
        );
      })()}

      {/* Keep all existing modals */}
      {showBulkEditModal && (() => {
        const visibleEditableFields = BULK_EDITABLE_FIELDS
          .filter((f) => visibleColumns.has(f.key))
          .filter((f) => canEditCost || !COST_FIELD_KEYS.has(f.key))
          .filter(
            (f) => canEditRegularPrice || !REGULAR_PRICE_FIELD_KEYS.has(f.key),
          )
          .filter(
            (f) => canEditSalePrice || !SALE_PRICE_FIELD_KEYS.has(f.key),
          )
          .map((f) => (
            f.optionsKey === "type"
              ? { ...f, options: dropdownOptions.type.map((o) => ({ value: o.name, label: o.name })) }
              : f
          ));

        return (
          <div className="modal-backdrop-bulk">
            <div
              className="modal-bulk"
              style={{
                width: "75%",
                maxWidth: "900px",
                maxHeight: "85vh",
                overflowX: "hidden",
                overflowY: "auto",
              }}
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-0">Advanced Bulk Edit</h5>
                  <small className="text-muted">Check a field to enable it — only checked fields will be applied.</small>
                </div>
                <button className="btn-close" onClick={() => setShowBulkEditModal(false)} />
              </div>

              {visibleEditableFields.length === 0 ? (
                <div className="text-center text-muted py-4 border rounded bg-light">
                  <div className="mb-2" style={{ fontSize: "15px", fontWeight: 500 }}>
                    No editable columns are visible.
                  </div>
                  <small>Enable columns from the Columns picker first, then reopen Advanced Bulk Edit.</small>
                </div>
              ) : (
                <div className="bulk-edit-fields">
                  <div className="bulk-edit-fields__head">
                    <div className="bulk-edit-fields__check" />
                    <div className="bulk-edit-fields__label">Field</div>
                    <div className="bulk-edit-fields__value">Value</div>
                  </div>
                  {visibleEditableFields.map(({ key, label, type, options }) => {
                    const isEnabled = key in bulkEditValues;
                    const value = bulkEditValues[key] ?? "";
                    return (
                      <div key={key} className="bulk-edit-fields__row">
                        <div className="bulk-edit-fields__check">
                          <input
                            type="checkbox"
                            className="form-check-input mt-0"
                            id={`bulk-${key}`}
                            checked={isEnabled}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setBulkEditValues((prev) => {
                                const updated = { ...prev };
                                if (checked) updated[key] = "";
                                else delete updated[key];
                                return updated;
                              });
                            }}
                          />
                        </div>
                        <label
                          htmlFor={`bulk-${key}`}
                          className="bulk-edit-fields__label"
                          style={{ fontWeight: isEnabled ? 600 : 400 }}
                        >
                          {label}
                        </label>
                        <div className="bulk-edit-fields__value">
                          {type === "select" ? (
                            <select
                              className="form-select form-select-sm"
                              value={value}
                              disabled={!isEnabled}
                              onChange={(e) => setBulkEditValues((prev) => ({ ...prev, [key]: e.target.value }))}
                            >
                              <option value="">— Select —</option>
                              {(options || []).map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={type}
                              className="form-control form-control-sm"
                              value={value}
                              disabled={!isEnabled}
                              placeholder={isEnabled ? `Enter ${label}` : ""}
                              onChange={(e) => setBulkEditValues((prev) => ({ ...prev, [key]: e.target.value }))}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-3 d-flex gap-2">
                <button
                  className="btn btn-primary"
                  onClick={handleApplyBulkEdit}
                  disabled={visibleEditableFields.length === 0}
                >
                  Apply
                </button>
                <button className="btn btn-danger" onClick={() => setShowBulkEditModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

      {showColumnModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Column Visibility</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowColumnModal(false)}
                ></button>
              </div>

              <div className="modal-body">
                <div className="row">
                  {columns.map((column) => (
                    <div key={column.key} className="col-6">
                      <div className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={column.key}
                          checked={visibleColumns.has(column.key)}
                          onChange={() => handleColumnToggle(column.key)}
                        />
                        <label
                          className="form-check-label d-flex align-items-center"
                          htmlFor={column.key}
                        >
                          <i
                            className={`bi ${
                              visibleColumns.has(column.key)
                                ? "bi-eye"
                                : "bi-eye-slash"
                            } me-2`}
                          ></i>
                          {column.label}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowColumnModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCatSubPopup && (
        <div
          className="modal fade show"
          tabIndex="-1"
          role="dialog"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select Category & Sub Category</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCatSubPopup(false);
                    setTempCategory([]);
                    setTempSubCategory([]);
                    setCurrentEditingRow(null);
                  }}
                ></button>
              </div>

              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-bold">Select Category</label>
                  <Select
                    isMulti
                    value={dropdownOptions.category
                      ?.filter((opt) => tempCategory.includes(opt.name))
                      .map((opt) => ({
                        value: opt.name,
                        label:
                          catSubLang === "ar"
                            ? opt.ar_name || opt.name
                            : opt.name,
                      }))}
                    options={dropdownOptions.category?.map((opt) => ({
                      value: opt.name,
                      label:
                        catSubLang === "ar" ? opt.ar_name || opt.name : opt.name,
                    }))}
                    onChange={(selected) => {
                      const selectedNames = selected.map((s) => s.value);
                      setTempCategory(selectedNames);

                      const selectedIDs = dropdownOptions.category
                        .filter((opt) => selectedNames.includes(opt.name))
                        .map((opt) => opt.id);
                      const payload = {
                        category_ids: selectedIDs.join(","),
                        page: 1,
                        search_key: "",
                        status: "",
                      };
                      axiosInstance
                        .get("admin/sub-categories", { params: payload })
                        .then((res) => {
                          setDropdownOptions((prev) => ({
                            ...prev,
                            sub_category: res.data.response || [],
                          }));
                        });
                    }}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Select Sub Category
                  </label>
                  <Select
                    isMulti
                    value={dropdownOptions.sub_category
                      ?.filter((opt) => tempSubCategory.includes(opt.title))
                      .map((opt) => ({
                        value: opt.title,
                        label:
                          catSubLang === "ar"
                            ? opt.ar_title || opt.title
                            : opt.title,
                      }))}
                    options={dropdownOptions.sub_category?.map((opt) => ({
                      value: opt.title,
                      label:
                        catSubLang === "ar"
                          ? opt.ar_title || opt.title
                          : opt.title,
                    }))}
                    onChange={(selected) => {
                      setTempSubCategory(selected.map((s) => s.value));
                    }}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    const catValue = tempCategory.join(", ");
                    const subCatValue = tempSubCategory.join(", ");

                    // Look up Arabic names for display
                    const arCatNames = tempCategory
                      .map((name) => dropdownOptions.category?.find((o) => o.name === name)?.ar_name || "")
                      .filter(Boolean)
                      .join(", ");
                    const arSubCatNames = tempSubCategory
                      .map((title) => dropdownOptions.sub_category?.find((o) => o.title === title)?.ar_title || "")
                      .filter(Boolean)
                      .join(", ");

                    setProducts((prev) =>
                      prev.map((p) =>
                        p.id === currentEditingRow
                          ? {
                              ...p,
                              category: catValue,
                              sub_category: subCatValue,
                              ar_category: arCatNames,
                              ar_sub_category: arSubCatNames,
                            }
                          : p
                      )
                    );

                    // Track edits so handleSubmit includes them in the Excel
                    setEditedFields((prev) => {
                      const rowEdits = new Set(prev[currentEditingRow] || []);
                      rowEdits.add("category");
                      rowEdits.add("sub_category");
                      if (arCatNames) rowEdits.add("ar_category");
                      if (arSubCatNames) rowEdits.add("ar_sub_category");
                      return { ...prev, [currentEditingRow]: rowEdits };
                    });

                    // Auto-select the row
                    setSelectedRows((prev) => {
                      if (prev.has(currentEditingRow)) return prev;
                      const updated = new Set(prev);
                      updated.add(currentEditingRow);
                      return updated;
                    });

                    setShowCatSubPopup(false);
                  }}
                >
                  Done
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCatSubPopup(false);
                    setTempCategory([]);
                    setTempSubCategory([]);
                    setCurrentEditingRow(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {bulkUpdateErrors.length > 0 && (
        <TaxonomyErrorReport
          errors={bulkUpdateErrors}
          title={`${bulkUpdateErrors.length} row(s) failed to update`}
          subtitle="The rows below were not updated. Fix the taxonomy values (or other errors) listed here, then re-submit — other rows in this batch were saved."
          onClose={() => setBulkUpdateErrors([])}
        />
      )}

      {cellEditor && (
        <CellEditorModal
          editor={cellEditor}
          onApply={applyCellEditor}
          onClose={closeCellEditor}
        />
      )}

      {showFileUpdate && (
        <BulkUpdateFromFile
          dropdownOptions={dropdownOptions}
          canEditCost={canEditCost}
          canEditRegularPrice={canEditRegularPrice}
          canEditSalePrice={canEditSalePrice}
          onClose={() => setShowFileUpdate(false)}
          onComplete={() => fetchProducts(filters)}
        />
      )}
    </div>
  );
};

export default BulkEdit;
